import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { BarChart, Activity, PieChart, Loader2 } from 'lucide-react';
import apiService from '@/services/api';
import { Dashboard } from '@/types';
import { toast } from 'sonner';

const DashboardHome = () => {
    const navigate = useNavigate();
    const [dashboards, setDashboards] = useState<Dashboard[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isCreating, setIsCreating] = useState(false);

    useEffect(() => {
        loadDashboards();
    }, []);

    const loadDashboards = async () => {
        try {
            const data = await apiService.getDashboards();
            setDashboards(data);
        } catch (error) {
            console.error('Failed to load dashboards:', error);
            toast.error('Failed to load dashboards');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateDashboard = async () => {
        if (isCreating) return;
        console.log('Creating new dashboard...');
        setIsCreating(true);
        try {
            console.log('Sending request to API...');
            const newDash = await apiService.createDashboard({
                title: 'New Dashboard',
                description: 'Created via Analytics Home',
                is_public: false
            });
            console.log('Dashboard created successfully:', newDash);
            toast.success('Dashboard created!');
            navigate(`/analytics/dashboard/${newDash.id}`);
        } catch (error) {
            console.error('Failed to create dashboard:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            toast.error(`Failed to create dashboard: ${errorMessage}`);
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <div className="container mx-auto p-6 space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">Analytics</h1>
                    <p className="text-muted-foreground mt-2">
                        Visualize your data and manage dashboards.
                    </p>
                </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
                <Card
                    className={cn(
                        "hover:bg-muted/50 transition-colors cursor-pointer border-dashed relative",
                        isCreating && "opacity-50 pointer-events-none"
                    )}
                    onClick={handleCreateDashboard}
                >
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            Create New Dashboard
                        </CardTitle>
                        {isCreating ? (
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : (
                            <PieChart className="h-4 w-4 text-muted-foreground" />
                        )}
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{isCreating ? '...' : '+'}</div>
                        <p className="text-xs text-muted-foreground">
                            {isCreating ? 'Creating...' : 'Start from scratch'}
                        </p>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            Total Dashboards
                        </CardTitle>
                        <BarChart className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">{dashboards.length}</div>
                        <p className="text-xs text-muted-foreground">
                            Active dashboards
                        </p>
                    </CardContent>
                </Card>

                {/* Recent Activity Card (Static for now) */}
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">
                            Recent Activity
                        </CardTitle>
                        <Activity className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">-</div>
                        <p className="text-xs text-muted-foreground">
                            No recent activity
                        </p>
                    </CardContent>
                </Card>
            </div>

            <h2 className="text-xl font-semibold tracking-tight mt-6">My Dashboards</h2>
            {isLoading ? (
                <div className="flex justify-center p-12">Loading...</div>
            ) : dashboards.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-[200px] border rounded-lg bg-muted/10">
                    <p className="text-muted-foreground">No dashboards found. Create one to get started.</p>
                </div>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {dashboards.map(dashboard => (
                        <Card
                            key={dashboard.id}
                            className="text-card-foreground hover:shadow-lg transition-all cursor-pointer"
                            onClick={() => navigate(`/analytics/dashboard/${dashboard.id}`)}
                        >
                            <CardHeader>
                                <CardTitle className="text-lg">{dashboard.title}</CardTitle>
                                <CardDescription className="line-clamp-2">
                                    {dashboard.description || 'No description'}
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="text-xs text-muted-foreground">
                                    Last updated: {new Date(dashboard.updated_at || dashboard.created_at).toLocaleDateString()}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DashboardHome;
